import type { Engine } from '../types';
import { buildUrl } from '../url';

const GENRES = [
  'action',
  'adventure',
  'animation',
  'biography',
  'comedy',
  'crime',
  'documentary',
  'drama',
  'family',
  'fantasy',
  'history',
  'horror',
  'music',
  'mystery',
  'romance',
  'sci-fi',
  'sport',
  'thriller',
  'war',
  'western',
];

export const imdb: Engine = {
  id: 'imdb',
  name: 'IMDb',
  bang: 'imdb',
  aliases: [],
  homepage: 'https://www.imdb.com',
  category: 'Media',
  experimental: true,
  description: 'Advanced title search by type, genre, years and rating.',
  params: [
    {
      id: 'type',
      type: 'choice',
      label: 'Type',
      group: 'Type',
      options: [
        { value: 'feature', token: 'movie', label: 'Movies' },
        { value: 'tv_series', token: 'tv', label: 'TV series' },
        { value: 'tv_miniseries', token: 'mini', label: 'Miniseries' },
        { value: 'tv_movie', token: 'tvmovie', label: 'TV movies' },
        { value: 'short', token: 'short', label: 'Shorts' },
        { value: 'video_game', token: 'game', label: 'Video games' },
      ],
    },
    {
      id: 'genre',
      type: 'choice',
      label: 'Genre',
      group: 'Filters',
      options: GENRES.map(g => ({
        value: g,
        token: g.replace('-', ''),
        label: g[0]?.toUpperCase() + g.slice(1),
      })),
    },
    {
      id: 'years',
      type: 'range',
      prefix: 'y',
      label: 'Release years',
      group: 'Filters',
      help: 'y1990-1999, y2010- (2010 on), y1980 (up to 1980).',
    },
    {
      id: 'rating',
      type: 'number',
      prefix: 'r',
      label: 'Min rating',
      group: 'Filters',
      placeholder: '7',
      help: 'r7 = rated 7 or higher.',
    },
    {
      id: 'votes',
      type: 'number',
      prefix: '*',
      label: 'Min votes',
      group: 'Filters',
      placeholder: '10000',
      help: 'Filters out obscure titles with a handful of ratings.',
    },
    {
      id: 'sort',
      type: 'choice',
      label: 'Sort',
      group: 'Sort',
      options: [
        { value: 'moviemeter,asc', token: 'pop', label: 'Popularity' },
        { value: 'user_rating,desc', token: 'rated', label: 'Rating' },
        { value: 'num_votes,desc', token: 'votes', label: 'Number of votes' },
        { value: 'release_date,desc', token: 'newest', label: 'Newest' },
      ],
    },
  ],
  build({ query, values }) {
    const years = typeof values.years === 'object' ? values.years : undefined;
    return buildUrl('https://www.imdb.com/search/title/', {
      title: query,
      title_type: values.type as string | undefined,
      genres: values.genre as string | undefined,
      release_date: years
        ? `${years.min !== undefined ? `${years.min}-01-01` : ''},${years.max !== undefined ? `${years.max}-12-31` : ''}`
        : undefined,
      user_rating:
        typeof values.rating === 'number' ? `${values.rating},` : undefined,
      num_votes:
        typeof values.votes === 'number' ? `${values.votes},` : undefined,
      sort: values.sort as string | undefined,
    });
  },
};

export const archive: Engine = {
  id: 'archive',
  name: 'Internet Archive',
  bang: 'ia',
  aliases: ['archive'],
  homepage: 'https://archive.org',
  category: 'Media',
  description: 'Books, films, audio, software and live concerts.',
  params: [
    {
      id: 'media',
      type: 'choice',
      label: 'Media type',
      group: 'Type',
      options: [
        { value: 'texts', token: 'texts', label: 'Texts' },
        { value: 'movies', token: 'video', label: 'Video' },
        { value: 'audio', token: 'audio', label: 'Audio' },
        { value: 'etree', token: 'live', label: 'Live concerts' },
        { value: 'software', token: 'software', label: 'Software' },
        { value: 'image', token: 'images', label: 'Images' },
      ],
    },
    {
      id: 'years',
      type: 'range',
      prefix: 'y',
      label: 'Years',
      group: 'Filters',
      help: 'y1970-1979, y1990- (1990 on).',
    },
    {
      id: 'fulltext',
      when: { param: 'media', in: ['texts'], orUnset: true },
      whenLabel: 'only applies to texts',
      type: 'flag',
      token: 'inside',
      label: 'Search inside texts',
      group: 'Filters',
    },
    {
      id: 'sort',
      type: 'choice',
      label: 'Sort',
      group: 'Sort',
      options: [
        { value: '-downloads', token: 'views', label: 'Most views' },
        { value: '-week', token: 'week', label: 'Popular this week' },
        { value: '-date', token: 'newest', label: 'Newest published' },
        { value: '-addeddate', token: 'added', label: 'Recently added' },
        { value: 'title', token: 'title', label: 'Title A–Z' },
      ],
    },
  ],
  build({ query, values }) {
    const url = new URL('https://archive.org/search');
    url.searchParams.set('query', query);
    if (typeof values.media === 'string')
      url.searchParams.append('and[]', `mediatype:"${values.media}"`);
    if (typeof values.years === 'object') {
      const { min, max } = values.years;
      url.searchParams.append(
        'and[]',
        `year:[${min ?? '0000'} TO ${max ?? '9999'}]`
      );
    }
    if (values.fulltext === true) url.searchParams.set('sin', 'TXT');
    if (typeof values.sort === 'string')
      url.searchParams.set('sort', values.sort);
    return url.toString();
  },
};
